import { Row } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router-dom'

import { Switch } from 'components/Switch'
import { TooltipProps } from 'components/Tooltip'
import { KNOWLEDGE_BASE_URL } from 'features/inventory/ad-unit/constants'
import { usePriceGeniusAlwaysOn } from 'features/inventory/ad-unit/hooks/usePriceGeniusAlwaysOn'
import { Product } from 'features/product'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { useAbility } from 'providers/casl'
import { ROUTES } from 'routes'
import { AdUnitForTable } from '../../types'
import { useSwitchAdUnitProduct } from './hooks/useSwitchAdUnitProduct'
import { ProductSwitchCellProps } from './types'
import { useSnackbar } from './useSnackbar'

export const ProductSwitchCell = ({
  column: { id: columnId },
  disabled,
  getValue,
  product,
  row: {
    index,
    original: { id, outOfPage, path },
  },
  table,
}: ProductSwitchCellProps) => {
  const ability = useAbility()
  const { t } = useTranslation(['features/adUnit', 'features/product', 'common'])
  const { workspaceId } = useWorkspaceParam()
  const { overwritePriceGeniusState, priceGeniusAlwaysOn } = usePriceGeniusAlwaysOn()

  const { update } = useSwitchAdUnitProduct()
  const { snackbar } = useSnackbar({ path, product })

  const noProductAccess: boolean = ability.cannot('access', `Product-${product}`)

  const tryUpdateRelatedAdUnits = (value: boolean): void => {
    const products: Product[] = ['viewabilityTools', 'priceGenius']
    const related: Row<AdUnitForTable>[] = table
      .getCoreRowModel()
      .rows.filter(
        ({ original }: Row<AdUnitForTable>): boolean =>
          products.includes(product) && original.path === path && original.id !== id,
      )

    table.options.meta?.updateData?.(
      related.map((row: Row<AdUnitForTable>) => ({ columnId, rowIndex: row.index, value })),
    )
  }

  const updateToggle = (value: boolean): void => {
    const data = [{ columnId, rowIndex: index, value }]

    table.options.meta?.updateData?.(data)
    tryUpdateRelatedAdUnits(value)
  }

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const value: boolean = event.target.checked
    const previousValue: boolean = !value

    updateToggle(value)

    const { data } = await update({ id, product, value })
    const { errors } = { ...data?.updateAdUnitProductEnabled }

    if (errors && errors.length > 0) {
      updateToggle(previousValue)
      snackbar(value, 'error')
    } else {
      snackbar(value)
    }
  }

  const composeTooltip = (): TooltipProps | undefined => {
    if (noProductAccess) {
      return {
        content: t('features/product:featureUnavailable'),
      }
    } else if (product === 'priceGenius' && overwritePriceGeniusState(product)) {
      return {
        content: t('form.priceGenius.enabled.tooltip'),
        link: {
          title: t('common:learnMore'),
          url: KNOWLEDGE_BASE_URL,
        },
      }
    } else if (product === 'viewabilityTools' && outOfPage) {
      return {
        content: t('list.product.viewabilityTools.tooltip.content'),
        link: {
          title: t('list.product.viewabilityTools.tooltip.link'),
          url: generatePath(ROUTES.INVENTORY.AD_UNIT.EDIT, { id, tab: 'general', workspaceId }),
        },
      }
    } else return undefined
  }

  const composeValue = (): boolean => {
    if (noProductAccess) {
      return false
    } else if (overwritePriceGeniusState(product)) {
      return priceGeniusAlwaysOn
    } else if (product === 'viewabilityTools' && outOfPage) {
      return false
    } else {
      return Boolean(getValue())
    }
  }

  return (
    <Switch
      checked={composeValue()}
      disabled={disabled}
      name={columnId}
      onChange={handleChange}
      {...{
        tooltip: composeTooltip(),
        tooltipMode: 'wrap',
      }}
    />
  )
}
